import React, { useEffect, useMemo, useState } from 'react';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Toolbar } from 'primereact/toolbar';
import { useToast } from '../../../components/Toast';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import RequiredLabel from '../../../components/RequiredLabel';
import { QRCodeCanvas } from 'qrcode.react';
import { Dropdown } from 'primereact/dropdown';
import ASTInstrumentService from '../../../services/ast/ASTInstrumentService';
import { useDialog } from '../../../components/Dialog';
import { InputSwitch } from 'primereact/inputswitch';
import sparepartService from './sparepart-service';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';


const SparepartEdit: React.FC = () => {

  const [clientState, setClientState] = useState<any>(null);
  const navigate = useNavigate();
  const { showConfirmDialog } = useDialog();
  const { showToast } = useToast();
  const location = useLocation();
  const selectedItem = location.state?.selectedItem;
  const isEditMode = !!selectedItem;
  const [entity, setEntity] = useState<any>(null);  // Track selected entity
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState<any>({
    insertDate: new Date(),
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const requiredFields = ["itemSn", "itemDesc", "itemSku", "itemEntity"];

  const [entityDropdown, setEntityDropdown] = useState<any[]>([]);
  const entityDropdownOptions = useMemo(() => entityDropdown, [entityDropdown]);

  const [equipmentTypeDropdown, setEquipmentTypeDropdown] = useState<any[]>([]);
  const equipmentTypeDropdownOptions = useMemo(() => equipmentTypeDropdown, [equipmentTypeDropdown]);

  const [stateDropdown, setStateDropdown] = useState<any[]>([]);
  const stateDropdownOptions = useMemo(() => stateDropdown, [stateDropdown]);


  const defaultData = {
    itemEntity: "BMS",
    status: "Active",
  };

  useEffect(() => {
    const fetchClientState = async () => {
      try {
        const clientStateItem = await sparepartService.getClientState();
        setClientState(clientStateItem);

        if (isEditMode) {
          setFormData(selectedItem);
        } else {
          setFormData(defaultData);
        }

        ASTInstrumentService.getDropdown('AST', 'ENTITY').then((data) => {
          setEntityDropdown(data);
        });


      } catch (error) {
        showToast({
          severity: 'error',
          summary: 'Error',
          detail: error instanceof Error ? error.message : String(error),
        });
      }
    };

    fetchClientState();
  }, []);

  const toolbarTemplate = () => {

    const handleBack = () => {
      navigate(`/sparepart/sparepart`);
    }

    async function submit(): Promise<void> {

      let newErrors: { [key: string]: string } = {};
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          newErrors[field] = "This is a required field.";
          showToast(clientState.required);
        }
      });

      setErrors(newErrors);
      if (Object.keys(newErrors).length === 0) {
        const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

        formData.modifiedBy = clientState.fullName;
        formData.facilityCode = clientState.facilityCode;


        if (!isEditMode) {

          formData.insertBy = clientState.fullName;
          console.log('formData', formData);
          sparepartService.create(formData).then(() => {
            showToast(clientState.success);
            navigate(-1);

          }).catch((error: any) => {

            clientState.error.detail = error;
            showToast(clientState.error);
          });
        }
        else {
          sparepartService.update(formData).then(() => {
            navigate(-1);
            showToast(clientState.success);

          }).catch((error: any) => {
            clientState.error.detail = error.response?.data?.message || 'Failed to update record.';
            showToast(clientState.error);
          });
        }
      }
    }


    return (

      <>

        <Button label={t('save')} icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={submit}></Button>
        <Button label={t('back')} icon="pi pi-reply" style={{ marginRight: '.5em' }} onClick={handleBack}></Button>

      </>
    );
  };

  const handleUpperCaseInputChange = (e: any) => {
    const { id, value } = e.target;

    setFormData({
      ...formData,
      // [id]: value.replace(/\D/g, '')
      [id]: value.toUpperCase()
    });
  };

  const handleInputChange = (e: any) => {
    const { id, value } = e.target;
    // const numericValue = value.replace(/\D/g, ''); // removes all non-digit characters

    setFormData({
      ...formData,
      // [id]: numericValue
      [id]: value
    });
  };


  const handleDropdownChangeCollection = (e: any) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
  };

  const handleSnNoBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const snNo = event.target.value;

    if (snNo) {

    }
  };



  const renderQRCode = (rowData: any) => {
    return (
      <div className="flex justify-center p-4">
        <QRCodeCanvas value={rowData?.qrValue} size={180} />
      </div>
    );
  };

  return (
    <div className="grid">
      <div className='col-12'>
        <div className='card-title'>
          <h5>Create Spare Part Form</h5>
          <BreadcrumbComponent />
        </div>
        <div className='form-title'>
          <Fieldset legend="Create Spare Part Form">
            <div className="field col-12 flex justify-content-center">
              {formData?.qrValue ? renderQRCode(formData) : null}
            </div>
            <div className="p-fluid form-grid-edit grid">
              <div className="f-field">
                {/* <label htmlFor="entity">Entity</label> */}
                <RequiredLabel text="Entity" required />
                <div className="f-input">
                  <Dropdown
                    id="itemEntity"
                    value={formData?.itemEntity || ''}
                    onChange={handleDropdownChangeCollection}
                    options={entityDropdownOptions}
                    optionLabel="fullDesc"
                    placeholder="Select One"
                    optionValue="referenceCode"
                  />
                  {errors.itemEntity && <small className="p-error">{errors.itemEntity}</small>}
                </div>
              </div>

              <div className="f-field">
                <RequiredLabel text="Serial no" required />
                <div className="f-input">
                  <InputText
                    id="itemSn"
                    value={formData?.itemSn || ''}
                    onChange={!isEditMode ? handleUpperCaseInputChange : undefined}
                    onBlur={!isEditMode ? handleSnNoBlur : undefined}
                    readOnly={!!isEditMode}
                  />
                  {errors.itemSn && <small className="p-error">{errors.itemSn}</small>}
                </div>
              </div>

              <div className="f-field">
                <RequiredLabel text="SKU" required />
                <div className="f-input">
                  <InputText
                    id="itemSku"
                    value={formData?.itemSku || ''}
                    onChange={!isEditMode ? handleInputChange : undefined}
                    readOnly={!!isEditMode}
                  />
                  {errors.itemSku && <small className="p-error">{errors.itemSku}</small>}
                </div>
              </div>


              <div className="f-field">
                <RequiredLabel text="Item Description" required />
                <div className="f-input">
                  <InputText
                    id="itemDesc"
                    value={formData?.itemDesc || ''}
                    onChange={handleInputChange}
                  />
                  {errors.itemDesc && <small className="p-error">{errors.itemDesc}</small>}
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Brand</label>
                <div className="f-input">
                  <InputText
                    id="itemBrand"
                    value={formData?.itemBrand || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="f-field">
                <label className='f-label'>Location</label>
                <div className="f-input">
                  <InputText
                    id="itemLocation"
                    value={formData?.itemLocation || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Item Quantity</label>
                <div className="f-input">
                  <InputNumber
                    inputId="itemQuantity"
                    name="itemQuantity"
                    showButtons
                    mode="decimal"
                    value={formData?.itemQuantity || 0}
                    onValueChange={(e) => setFormData({ ...formData, itemQuantity: e.value })}
                    disabled={isEditMode}  // Disable the input if in edit mode
                  />
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Status</label>
                <div className="f-input">
                  <InputSwitch
                    checked={formData?.status === 'Active'}
                    onChange={(e) => setFormData({ ...formData, status: e.value ? "Active" : "Inactive" })}
                  />
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Remark</label>
                <div className="f-input">
                  <InputTextarea
                    id="itemRemark"
                    value={formData?.itemRemark || ''}
                    onChange={handleInputChange}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </Fieldset>
          <div>
            <br />
          </div>

          <div>
            <br />
          </div>
          <Toolbar end={toolbarTemplate}></Toolbar>
        </div>
      </div>
    </div>
  );
};

export default SparepartEdit;
